/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  1. 프로그램명 : 로그인
//  2. 프로그램ID : LoginPage
//  3. 주요설명 : 로그인 페이지
//  4. 개발이력 : 2025.08.17  최초개발
//               2026.09.21  화면 디자인을 모던하게 개편(브랜드 패널+폼 분할, 모바일은 시트형, 다크모드/모션최소화 대응).
//                           로그인/언어/비밀번호변경 동작은 그대로이고 마크업+CSS(loginModern.css)만 변경.
//               2026.09.21  "이번 달 시책" 광고 카드(LoginPromo) 추가 - A: 브랜드 패널/좁은 화면은 폼 아래, B: 유리 카드 아래.
//               2026.09.26  회사 선택 콤보 추가(SaaS 멀티테넌시) - 목록은 서버(apiCompanyList)에서 받음(utils/companyUtil.ts), 선택값은 localStorage(coCd)에
//                           저장되어 모든 요청의 X-Co-Cd 헤더로 전송됨(axiosSetup). 나중에 도메인으로 회사를 식별하게 되면 대체 예정.
//               2026.09.28  디자인 B(다크 오로라 + 리퀴드 글래스 카드, ?style=aurora)를 완전히 제거 - loginAurora.css 삭제,
//                           LoginPromo의 'aurora' variant 및 관련 스타일도 함께 정리(현재는 분할형 디자인 A만 사용).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 사용자정의 모듈
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { API_BASE_URL } from '../config'; // API 기본 URL
import { getClientInfo } from '../utils/clientInfoUtil'; // 로그인 이력용 접속 환경(화면/시간대/언어/플랫폼)
import '../styles/loginModern.css'; // 로그인 화면 디자인 A(분할형, lp- 접두어). 비밀번호 변경 팝업용 loginPage.css는 ChangePasswordModal이 직접 import함
import { useCommonTranslation } from '../translations';
import { setLocalStorage } from '../utils/userUtils';
import { syncMainframeTheme } from '../styles/mainframeTheme'; // 로그인 직후 적용중 메인프레임 테마(V0/V1...) 확인
import { useMsgBoxModal } from '../components/CommonModal';
import { getMsgText, getMsgType } from '../utils/commonUtils';
import ChangePasswordModal from '../components/ChangePasswordModal';
import MfaModal from '../components/MfaModal'; // 2차인증(등록/검증) 팝업
import PwdResetModal from '../components/PwdResetModal'; // 비밀번호 찾기(OTP 또는 이메일 코드로 본인확인 후 새 비밀번호)
import { getLoginCompanies, loadLoginCompanies, getSelectedCoCd, setSelectedCoCd } from '../utils/companyUtil'; // 로그인 회사 선택

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 로그인 브랜드 소개
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import LoginPromo from '../components/LoginPromo'; // "이번 달 시책" 광고 카드(HomePageU1 시책 배너와 같은 데이터)
import LoginGainsBrand from '../components/LoginGainsBrand'; // 좌측 브랜드 패널 - GAINS 소개 아티팩트
import LoginHelpModal from '../components/LoginHelpModal'; // 로그인 도움말(비밀번호변경/2차인증/로그인안될때) 팝업

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 쿠키 조작 함수
////////////////////////////////////////////////////////////////////////////////////////////////////////////
function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/';
}
function getCookie(name: string) {
  return document.cookie.split('; ').reduce((r, v) => {
    const parts = v.split('=');
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, '');
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 메인컴포넌트 : LoginPage
////////////////////////////////////////////////////////////////////////////////////////////////////////////
const LoginPage: React.FC = () => {
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 0. 공통정의
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [coCd, setCoCd] = useState<string>(() => getSelectedCoCd());   // 로그인 회사(선택값은 localStorage에 저장 - 모든 요청 X-Co-Cd 헤더)
  const changeCoCd = (v: string) => { setCoCd(v); setSelectedCoCd(v); };
  const [companies, setCompanies] = useState(() => getLoginCompanies());   // 회사 목록(서버 ZT_TENANT의 사용 중인 회사 - 회사등록 ZX102M1에서 추가하면 자동 반영)
  useEffect(() => {
    let alive = true;
    loadLoginCompanies().then(list => {
      if (!alive) return;
      setCompanies(list);
      const sel = getSelectedCoCd();   // 저장돼 있던 회사가 목록에서 사라졌으면 첫 번째 회사로
      setCoCd(sel); setSelectedCoCd(sel);
    });
    return () => { alive = false; };
  }, []);
  const [saveLoginInfo, setsaveLoginInfo] = useState(false);
  const [passWord, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false); // 비밀번호 표시/숨김 토글(눈 아이콘)
  const [loginHelpOpen, setLoginHelpOpen] = useState(false); // 로그인 도움말(?) 팝업 표시 여부
  const { ddKey, language, getLanguage } = useCommonTranslation();
  const { showMsgBox } = useMsgBoxModal();
  const [cbLanguages, setLanguage] = useState<{ code: string; label: string; flag?: string }[]>([]);

  // 최초/강제 비밀번호 변경 팝업 상태 - apiAuthUser가 mustChangePwd:true를 내려주면 로그인을 완료하지
  // 않고 이 팝업을 띄운 뒤, 비밀번호 변경이 성공해야만 로그인을 이어서 완료함(completeLogin 참고)
  // 팝업 자체(입력폼/제출로직)는 ChangePasswordModal(공용 컴포넌트)로 분리됨 - mainFrame의 "비밀번호변경"도 동일 컴포넌트 사용.
  const [pwdChangeOpen, setPwdChangeOpen] = useState(false);
  const [pwdResetOpen, setPwdResetOpen] = useState(false);   // 비밀번호 찾기 팝업
  // PC 좌우 분할에서 우측 로그인 영역의 폭(px) - 손잡이로 드래그해서 조절, 브라우저에 저장해 다음 접속에도 유지
  const LP_FORM_W_DEFAULT = 400, LP_FORM_W_MIN = 320, LP_FORM_W_MAX = 900;
  const [formW, setFormW] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('lpFormWidth'));
      return v >= LP_FORM_W_MIN && v <= LP_FORM_W_MAX ? v : LP_FORM_W_DEFAULT;
    } catch { return LP_FORM_W_DEFAULT; }
  });
  useEffect(() => {
    try { localStorage.setItem('lpFormWidth', String(formW)); } catch { /* 저장 실패는 무시 */ }
  }, [formW]);
  const clampFormW = (w: number) => Math.max(LP_FORM_W_MIN, Math.min(Math.min(LP_FORM_W_MAX, Math.floor(window.innerWidth * 0.6)), w));
  // 손잡이는 우측 패널의 왼쪽 경계에 있으므로, 왼쪽으로 끌수록 로그인 영역이 넓어짐
  const startFormResize = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX, startW = formW;
    const onMove = (ev: PointerEvent) => setFormW(clampFormW(startW - (ev.clientX - startX)));
    const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const onFormResizeKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); setFormW(w => clampFormW(w + 16)); }
    if (e.key === 'ArrowRight') { e.preventDefault(); setFormW(w => clampFormW(w - 16)); }
  };
  const [pendingUserInfo, setPendingUserInfo] = useState<any>(null);

  // 2차인증(MFA) 팝업 상태 - apiAuthUser/apiChangePassword가 mfaRequired:true를 내려주면 토큰을 아직
  // 안 준 것이므로, 이 팝업(등록 또는 검증)을 마쳐야만 completeLogin으로 이어감(강제 비밀번호변경과 같은 자리).
  const [mfaState, setMfaState] = useState<{ stage: 'enroll' | 'verify'; pendingToken: string; hasEmail?: boolean; method?: string; allowRemember?: boolean; rememberDays?: number; expireMinutes?: number } | null>(null);

  // apiAuthUser/apiChangePassword 응답을 공용으로 처리 - mfaRequired면 MFA 팝업을, 아니면 completeLogin을 호출.
  // (강제 비밀번호변경은 이 처리보다 먼저 걸러짐 - handleLogin/handlePwdChangeSuccess 참고)
  const handleAuthOutcome = (userInfo: any, outcome: { tokenExpiresAt?: number; mfaRequired?: boolean; mfaStage?: 'enroll' | 'verify'; pendingToken?: string; hasEmail?: boolean; method?: string; allowRemember?: boolean; rememberDays?: number; expireMinutes?: number }) => {
    if (outcome?.mfaRequired && outcome.pendingToken && outcome.mfaStage) {
      setPendingUserInfo(userInfo);
      setMfaState({ stage: outcome.mfaStage, pendingToken: outcome.pendingToken, hasEmail: outcome.hasEmail, method: outcome.method, allowRemember: outcome.allowRemember, rememberDays: outcome.rememberDays, expireMinutes: outcome.expireMinutes });
      return;
    }
    completeLogin(userInfo, outcome?.tokenExpiresAt);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 1. 사용자이벤트 정의
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Load available cbLanguages from config.json
  //*LoginPage의 기본언어선택이 defaultLanguage를 우선 적용하고, 없으면 브라우저 언어로 기본 언어를 설정합니다
  // 다른 곳에서 같은 계정으로 로그인해 세션이 종료된 경우(중복로그인 허용=N) - axiosSetup이 남긴 안내를 한 번 보여줌
  useEffect(() => {
    try {
      const notice = sessionStorage.getItem('loginNotice');
      if (notice) {
        sessionStorage.removeItem('loginNotice');
        showMsgBox({ title: ddKey('lblAlertTitle') || '알림', msgText: notice, msgType: 'warning' });
      }
    } catch { /* 저장소 접근 실패는 무시 */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 페이지 진입 시 쿠키에서 userId 불러오기
  useEffect(() => {
    const savedId = getCookie('savedLoginInfo');
    if (savedId) {
      setUserId(savedId);
      setsaveLoginInfo(true);
    }
  }, []);

  useEffect(() => {
    fetch('/config.json')
      .then(res => res.json())
      .then(cfg => {
        if (Array.isArray(cfg.languages)) {
          setLanguage(cfg.languages);
          // 기본 언어 설정
          if (!language && cfg.defaultLanguage) {
            getLanguage(cfg.defaultLanguage);
          }
        }
      })
      .catch(() => {});
  }, [getLanguage, language]);

  // 세션이 있으면(만료시각 기록이 남아있으면) 자동 로그인 처리 - 실제 유효성은 세션쿠키로 서버가 검사함
  useEffect(() => {
    if (localStorage.getItem('tokenExpiresAt')) {
      navigate('/main');
    }
  }, [navigate]);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 2. 로그인 처리
  //    - handleLogin : Handle login submission (프론트엔드에서 임의 토큰 생성)
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 로그인 완료 처리(localStorage/쿠키 저장 + 메인이동) - 정상로그인과, 강제 비밀번호변경
  // 팝업에서 변경 성공한 직후 양쪽에서 공용으로 호출함. 세션토큰 자체는 apiAuthUser/apiChangePassword가
  // 성공 시 httpOnly 쿠키로 직접 심어주므로(2026-10-17), 여기서는 카운트다운 표시용 만료시각만 받아서
  // 저장함 - 별도로 토큰발급 API를 다시 호출하지 않음.
  const completeLogin = async (userInfo: any, tokenExpiresAt?: number) => {
    if (!tokenExpiresAt) {
      showMsgBox({ title: ddKey('lblAlertTitle') || '알림', msgText: ddKey('tokenIssueFailed') || '토큰발행 실패', msgType: 'error' });
      return;
    }
    setLocalStorage({
      userId: userInfo.userId,
      userName: userInfo.userName,
      email: userInfo.email,
      language,
      userKind: userInfo.userKind ?? '',
      sessExtYn: userInfo.sessExtYn ?? 'N',              // 사용자환경설정(Z3020M1) - 세션 연장 가능 여부
      sessLimitMin: userInfo.sessLimitMin ?? 60,         // 접속제한시간(분)
      noticeCoYn: userInfo.noticeCoYn ?? 'N',            // 공지권한(회사) - 공지 공유범위 제한
      noticeBuYn: userInfo.noticeBuYn ?? 'N',            // 공지권한(사업단)
      menuIdViewYn: userInfo.menuIdViewYn ?? 'N',        // 메뉴ID 보기
      serverInfo: userInfo.serverInfo ?? {},   // 서버 접속 정보(체크된 항목만) - 사용자 패널에 표시
      // 외부연동 계정(하이웍스/Gmail 등, ZA_USER_EXT_ACCT) - 로그인 응답에 이미 통째로 실려오므로
      // 매번 서버에 새로 물어보지 않고 로그인 시점에 한 번에 캐싱해둠(메일발송 등에서 사용).
      extAccts: userInfo.extAccts,
    });

    // userId 저장 체크 시 쿠키에 저장, 해제 시 삭제
    if (saveLoginInfo) {
      setCookie('savedLoginInfo', userId, 30);
      setCookie('savedLanguage', language, 30);
    } else {
      setCookie('savedLoginInfo', '', -1);
      setCookie('savedLanguage', '', -1);
    }

    localStorage.setItem('tokenExpiresAt', String(tokenExpiresAt));
    // 적용중 메인프레임 테마(V0/V1...) 확인 - 로드된 CSS와 다른 버전이면 화면을 새로 불러와 해당 CSS로 시작함
    if (await syncMainframeTheme(language)) {
      window.location.assign('/main');
      return;
    }
    // 메인 페이지로 이동
    navigate('/main');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 백엔드에 로그인인증 요청
      //console.log('getAuthUser() :', passWord);
      setSelectedCoCd(coCd);   // 헤더(X-Co-Cd)와 본문의 회사가 같도록 보내기 전에 한 번 더 저장
      const resAuthUser = await axios.post(`${API_BASE_URL}/com/apiAuthUser`, {langCd: language, coCd, userId: userId, passWord: passWord, clientInfo: getClientInfo(language) }, { withCredentials: true });
      const { userInfo, ...outcome } = resAuthUser.data;
      if (!userInfo) {
        // apiAuthUser는 실패 시 항상 비2xx로 응답(sendCodedMessage)하므로 정상적으로는 도달하지 않는 방어용 분기
        showMsgBox({ title: ddKey('lblAlertTitle') || '알림', msgText: ddKey('noUserInfo') || '사용자 정보가 없습니다.', msgType: 'warning' });
        return;
      }

      // 최초 로그인(관리자 임시비번 발급) 또는 비밀번호 만료 - 변경 전까지 로그인 완료를 보류하고 팝업 표시
      // (2차인증은 비밀번호 변경 이후로 미뤄짐 - handlePwdChangeSuccess 참고)
      if (userInfo.mustChangePwd) {
        setPendingUserInfo(userInfo);
        setPwdChangeOpen(true);
        return;
      }

      handleAuthOutcome(userInfo, outcome);
    } catch (error: any) {
      showMsgBox({ title: ddKey('lblAlertTitle') || '알림', msgText: getMsgText(error, ddKey('loginFailed') || '로그인 실패'), msgType: getMsgType(error) });
      return;
    } finally {
      setLoading(false);
    }
  };

  // 강제 비밀번호변경 팝업 - 제출 성공 시 곧바로 이어감(재로그인 불필요) - 2차인증이 필요하면 그 팝업으로,
  // 아니면 로그인 완료로.
  const handlePwdChangeSuccess = (outcome: { tokenExpiresAt?: number; mfaRequired?: boolean; mfaStage?: 'enroll' | 'verify'; pendingToken?: string; hasEmail?: boolean; method?: string; allowRemember?: boolean; rememberDays?: number; expireMinutes?: number }) => {
    setPwdChangeOpen(false);
    handleAuthOutcome(pendingUserInfo, outcome);
  };

  // 2차인증(등록/검증) 완료 - 서버가 이미 로그인을 완료(userInfo 반환 + 세션쿠키 발급)시켜준 상태이므로 그대로 이어감
  const handleMfaSuccess = (userInfo: any, tokenExpiresAt: number) => {
    setMfaState(null);
    completeLogin(userInfo, tokenExpiresAt);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 9. 화면정의
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  // 최초/강제 비밀번호 변경 팝업(두 디자인 공통)
  const pwdChangeModal = (
    <>
      {/* 최초/강제 비밀번호 변경 팝업 - mustChangePwd:true일 때만 표시. 변경 성공 전까지는 로그인이
          완료되지 않으며(토큰 미발급), 이 팝업을 닫아도(취소) 다시 로그인 폼으로 돌아갈 뿐 접속되지 않음.
          currentPassword를 넘겨서(방금 로그인폼에 입력한 값) "현재 비밀번호" 재입력을 요구하지 않음. */}
      <ChangePasswordModal
        open={pwdChangeOpen}
        userId={pendingUserInfo?.userId || userId}
        currentPassword={passWord}
        title={ddKey('lblPwdChangeTitle') || '비밀번호 변경'}
        description={ddKey('msgPwdChangeRequired') || '보안을 위해 비밀번호를 변경해야 합니다. 새 비밀번호를 입력하세요.'}
        submitLabel={ddKey('btnChangePasswordLogin') || '변경 후 로그인'}
        onClose={() => setPwdChangeOpen(false)}
        onSuccess={handlePwdChangeSuccess}
      />
      {/* 2차인증(등록/검증) 팝업 - mfaState가 있을 때만 표시. 여기서 닫으면(취소) 로그인 폼으로 돌아갈
          뿐 접속되지 않음(비밀번호변경 팝업과 동일한 성격). */}
      {mfaState && (
        <MfaModal
          open={!!mfaState}
          stage={mfaState.stage}
          pendingToken={mfaState.pendingToken}
          hasEmail={mfaState.hasEmail}
          method={mfaState.method as 'TOTP' | 'EMAIL' | undefined}
          allowRemember={mfaState.allowRemember}
          rememberDays={mfaState.rememberDays}
          expireMinutes={mfaState.expireMinutes}
          onClose={() => setMfaState(null)}
          onSuccess={handleMfaSuccess}
        />
      )}
    </>
  );

  return (
    <div className="lp-root" style={{ '--lp-form-w': `${formW}px`, '--lp-form-scale': String(formW / LP_FORM_W_DEFAULT) } as React.CSSProperties}>
      {/* 브랜드 패널(공용 슬롯, .lp-brand-slot) - 안에 넣는 컴포넌트만 바꿔 끼우면 되도록 CSS 클래스는
          컴포넌트 이름과 무관하게 고정함. 현재는 LoginGainsBrand(GAINS 소개 아티팩트 이식판) 사용 중.
          xGA 기본 브랜드 패널(LoginXGABrand)로 되돌리려면 아래 태그만 <LoginXGABrand />로 바꾸면 됨. */}
      <section className="lp-brand-slot">
        <LoginGainsBrand />
      </section>

      {/* 좌우 경계 손잡이 - 드래그로 우측 로그인 영역 폭 조절(더블클릭=기본값, 화살표 키=16px씩). PC에서만 보임 */}
      <div
        className="lp-resizer"
        role="separator"
        aria-orientation="vertical"
        aria-label="로그인 영역 폭 조절"
        aria-valuemin={LP_FORM_W_MIN}
        aria-valuemax={LP_FORM_W_MAX}
        aria-valuenow={formW}
        tabIndex={0}
        onPointerDown={startFormResize}
        onDoubleClick={() => setFormW(LP_FORM_W_DEFAULT)}
        onKeyDown={onFormResizeKey}
      />

      <main className="lp-form-panel">
        {/* 모바일 좌측상단 로고 + 우측상단 언어선택. 로고는 GAINS 아티팩트의 현재 로고 디자인
            (G[AI 강조]NS, 어두운 배지)을 그대로 재현함(2026-09-28). PC에서는 좌측 브랜드 패널에
            이미 같은 로고가 있으므로 로그인 영역 쪽 로고는 숨김(아래 데스크톱 미디어쿼리). */}
        <div className="lp-form-topbar">
          <span className="lp-form-logo" aria-label="GAINS">
            G<b>AI</b>NS
          </span>
          <div className="lp-lang">
            <i className="fas fa-globe" aria-hidden="true" />
            <select
              id="langSelect"
              aria-label={ddKey('language') || '언어'}
              value={language}
              onChange={(e) => getLanguage(e.target.value as any)}
              disabled={loading}
            >
              {cbLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag ? `${lang.flag} ` : ''}{lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="lp-card">
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
            <div>
              <h1 className="lp-title">{ddKey('loginTitle') || '로그인'}</h1>
              <p className="lp-subtitle">{ddKey('loginFooter') || 'GAINS에 오신 것을 환영합니다.'}</p>
            </div>
            <button
              type="button"
              onClick={() => setLoginHelpOpen(true)}
              aria-label={ddKey('loginHelpTooltip') || '로그인 도움말'}
              title={ddKey('loginHelpTooltip') || '로그인 도움말'}
              style={{
                flex: '0 0 auto', width: 30, height: 30, borderRadius: '50%', marginTop: 2,
                border: '1px solid var(--lp-field-border)', background: 'var(--lp-field)',
                color: 'var(--lp-primary)', fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <i className="fas fa-question" aria-hidden="true" />
            </button>
          </div>

          <form className="lp-form" onSubmit={handleLogin}>
            <div className="lp-field">
              <label htmlFor="coCd" className="lp-label">{ddKey('company') || '회사'}</label>
              <div className="lp-input-wrap">
                <i className="fas fa-building lp-ico" aria-hidden="true" />
                <select
                  id="coCd"
                  name="coCd"
                  className="lp-input lp-select"
                  value={coCd}
                  onChange={(e) => changeCoCd(e.target.value)}
                  disabled={loading}
                >
                  {companies.map(c => <option key={c.coCd} value={c.coCd}>{c.coNm}</option>)}
                </select>
              </div>
            </div>

            <div className="lp-field">
              <label htmlFor="userId" className="lp-label">{ddKey('userId') || '사용자 ID'}</label>
              <div className="lp-input-wrap">
                <i className="fas fa-user lp-ico" aria-hidden="true" />
                <input
                  id="userId"
                  name="userId"
                  type="text"
                  required
                  className="lp-input"
                  placeholder={ddKey('userIdPlaceholder')}
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={loading}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
            </div>

            <div className="lp-field">
              <label htmlFor="passWord" className="lp-label">{ddKey('passWord') || '비밀번호'}</label>
              <div className="lp-input-wrap">
                <i className="fas fa-lock lp-ico" aria-hidden="true" />
                <input
                  id="passWord"
                  name="passWord"
                  type={showPw ? 'text' : 'password'}
                  required
                  className="lp-input"
                  placeholder={ddKey('passWordPlaceholder')}
                  value={passWord}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="lp-eye"
                  onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? (ddKey('hidePassword') || '비밀번호 숨기기') : (ddKey('showPassword') || '비밀번호 표시')}
                  aria-pressed={showPw}
                  tabIndex={-1}
                  disabled={loading}
                >
                  <i className={`fas ${showPw ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                </button>
              </div>
            </div>

            <label className="lp-remember">
              <input
                type="checkbox"
                checked={saveLoginInfo}
                onChange={e => setsaveLoginInfo(e.target.checked)}
                disabled={loading}
              />
              {ddKey('saveLoginInfo') || '로그인 정보 저장'}
            </label>

            <button type="submit" className="lp-submit" disabled={loading}>
              {loading
                ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" />{ddKey('loggingIn') || '로그인 중...'}</>
                : <>{ddKey('btnLogin') || '로그인'}<i className="fas fa-arrow-right" aria-hidden="true" /></>}
            </button>
            <button type="button" className="lp-forgot" onClick={() => setPwdResetOpen(true)} disabled={loading}>
              {ddKey('lblForgotPassword') || '비밀번호를 잊으셨나요?'}
            </button>
          </form>

          <PwdResetModal open={pwdResetOpen} onClose={() => setPwdResetOpen(false)} />

          <LoginPromo variant="sheet" className="lp-promo-sheet" />
          <p className="lp-copy">© 2026 PEOPLESOFT · GAINS</p>
        </div>
      </main>

      {pwdChangeModal}
      <LoginHelpModal open={loginHelpOpen} onClose={() => setLoginHelpOpen(false)} />
    </div>
  );
};

export default LoginPage;
